/**
 * Utility functions for working with CSS custom properties (variables)
 * Especially useful for SVG elements that don't support var() in all browsers
 */

// Fallback values for theme colors in case CSS variables can't be resolved
const THEME_COLOR_FALLBACKS = {
  '--team-color-own': '#388e3c',
  '--team-color-ally': '#1976d2',
  '--team-color-enemy': '#ef4444',
  '--color-cyan': '#64FFDA',
  '--color-primary': '#d4a94f',
  '--color-secondary': '#388e3c',
  '--color-error': '#d32f2f',
  '--color-warning': '#ff9800',
  '--color-success': '#4caf50',
};

/**
 * Resolves a CSS custom property to its computed value
 * @param {string} cssVar - CSS variable name (e.g., '--team-color-own')
 * @returns {string} The resolved color value or fallback
 */
export function resolveCSSVariable(cssVar) {
  if (typeof window === 'undefined') {
    return THEME_COLOR_FALLBACKS[cssVar] || '#888888';
  }

  try {
    const computedStyle = getComputedStyle(document.documentElement);
    const value = computedStyle.getPropertyValue(cssVar).trim();
    
    // Return resolved value or fallback if empty
    return value || THEME_COLOR_FALLBACKS[cssVar] || '#888888';
  } catch (error) {
    console.warn(`[cssVariables] Failed to resolve ${cssVar}:`, error);
    return THEME_COLOR_FALLBACKS[cssVar] || '#888888';
  }
}

/**
 * Resolves multiple CSS variables at once
 * @param {string[]} cssVars - Array of CSS variable names
 * @returns {Object} Object with variable names as keys and resolved values
 */
export function resolveCSSVariables(cssVars) {
  const resolved = {};
  for (const cssVar of cssVars) {
    resolved[cssVar] = resolveCSSVariable(cssVar);
  }
  return resolved;
}

/**
 * Pre-configured team colors resolver for common use cases
 * @returns {Object} Resolved team colors object
 */
export function getTeamColors() {
  return {
    own: resolveCSSVariable('--team-color-own'),
    ally: resolveCSSVariable('--team-color-ally'),
    enemy: resolveCSSVariable('--team-color-enemy'),
    unknown: '#888888', // Gray for unknown teams
  };
}

/**
 * Get team color by team class
 * @param {string} teamClass - 'own', 'ally', 'enemy', or 'mixed'
 * @returns {string} The resolved color for the team
 */
export function getTeamColor(teamClass) {
  const teamColors = getTeamColors();
  
  switch (teamClass) {
    case 'own':
      return teamColors.own;
    case 'ally':
      return teamColors.ally;
    case 'enemy':
      return teamColors.enemy;
    case 'mixed':
      return '#333333'; // Dark gray for mixed teams
    default:
      return '#888888'; // Default gray
  }
}

/**
 * Common theme colors resolver
 * @returns {Object} Resolved common theme colors
 */
export function getThemeColors() {
  return {
    primary: resolveCSSVariable('--color-primary'),
    secondary: resolveCSSVariable('--color-secondary'),
    cyan: resolveCSSVariable('--color-cyan'),
    error: resolveCSSVariable('--color-error'),
    warning: resolveCSSVariable('--color-warning'),
    success: resolveCSSVariable('--color-success'),
    ...getTeamColors(),
  };
}